/**
 * API client utilities for HTTP communication with the server.
 * Framework-agnostic — provides typed request/response helpers.
 * Actual fetch implementation is platform-specific.
 */
// ─── Default Config ─────────────────────────────────────────────────
export const defaultApiConfig = {
    baseUrl: '/api',
    timeoutMs: 15_000,
    getAccessToken: () => null,
    onTokenRefresh: async () => null,
    onAuthFailure: () => { },
};
// ─── URL Helpers ────────────────────────────────────────────────────
/**
 * Build a full URL from base, path, and query params.
 */
export function buildUrl(baseUrl, path, params) {
    const url = `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    if (!params || Object.keys(params).length === 0)
        return url;
    const paramPairs = Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');
    return `${url}?${paramPairs}`;
}
/**
 * Build request headers including auth token.
 */
export function buildHeaders(config, options) {
    const headers = {
        Accept: 'application/json',
        ...options.headers,
    };
    if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    if (!options.skipAuth) {
        const token = config.getAccessToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
}
// ─── Error Handling ─────────────────────────────────────────────────
/** Human-readable fallback when the server sends no usable message. */
function defaultMessageForStatus(status) {
    switch (status) {
        case 400:
            return 'Some of the details you entered are not valid. Please check and try again.';
        case 401:
            return 'Your email or password is incorrect.';
        case 403:
            return "You don't have permission to do that.";
        case 404:
            return 'We could not find what you were looking for.';
        case 409:
            return 'That account already exists.';
        case 429:
            return 'Too many attempts. Please wait a little while and try again.';
        case 500:
        case 502:
        case 503:
        case 504:
            return 'The server is having trouble right now. Please try again in a moment.';
        default:
            return status === 0
                ? 'Could not reach the server. Check your internet connection and try again.'
                : `Request failed with status ${status}`;
    }
}
/** Field-error maps arrive as { password: ['Password must be...'], ... }. */
function isFieldErrorMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    return Object.values(value).every((v) => Array.isArray(v) && v.every((item) => typeof item === 'string'));
}
/**
 * Flatten a field-error map into readable lines, e.g.
 * "Password must be at least 8 characters".
 */
export function formatFieldErrors(details) {
    if (!isFieldErrorMap(details))
        return [];
    return Object.values(details).flat().filter(Boolean);
}
/**
 * Create a typed ApiError from a response status and body.
 *
 * Reads the canonical `error: { code, message, details }` envelope, then falls
 * back to the legacy top-level `message`/`errors` shape, then to the first
 * field-level validation message, and only then to a generic status message.
 * Without those fallbacks a 409 "Email already registered" surfaced to the user
 * as "Request failed with status 409".
 */
export function createApiError(status, body) {
    const details = body?.error?.details ?? body?.errors;
    const fieldMessages = formatFieldErrors(details);
    const message = body?.error?.message ?? body?.message ?? fieldMessages[0] ?? defaultMessageForStatus(status);
    return {
        status,
        code: body?.error?.code ?? (status === 0 ? 'NETWORK_ERROR' : 'UNKNOWN_ERROR'),
        message,
        details,
    };
}
/**
 * Whether a value is an ApiError produced by createApiError.
 */
export function isApiError(error) {
    return (typeof error === 'object' &&
        error !== null &&
        typeof error.status === 'number' &&
        typeof error.code === 'string' &&
        typeof error.message === 'string');
}
/**
 * Turn any thrown value into a message safe to show a user.
 *
 * For validation failures the field messages are appended, so the user sees
 * exactly which rule they broke rather than a bare "Validation failed".
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
    if (isApiError(error)) {
        const fieldMessages = formatFieldErrors(error.details);
        // "Validation failed" alone tells the user nothing — replace it with the
        // actual field rules when the server sent them.
        if (fieldMessages.length > 0) {
            const generic = /^validation failed$/i.test(error.message);
            return generic ? fieldMessages.join('\n') : [error.message, ...fieldMessages].join('\n');
        }
        return error.message || fallback;
    }
    if (error instanceof Error && error.message)
        return error.message;
    return fallback;
}
/**
 * Whether an error is a network/timeout error (vs server error).
 *
 * React Native's fetch rejects with `TypeError: Network request failed`, which
 * the old `includes('fetch')` check missed — offline registration attempts were
 * reported as generic failures instead of connection problems.
 */
export function isNetworkError(error) {
    if (error instanceof Error && error.name === 'AbortError')
        return true;
    if (error instanceof TypeError) {
        const message = error.message.toLowerCase();
        return (message.includes('fetch') ||
            message.includes('network request failed') ||
            message.includes('network error'));
    }
    return false;
}
/**
 * Whether a status code indicates the request should be retried.
 */
export function isRetryableStatus(status) {
    return status === 429 || status === 502 || status === 503 || status === 504;
}
/**
 * Whether a status is an auth error (token expired).
 */
export function isAuthError(status) {
    return status === 401;
}
// ─── Typed API Endpoints ────────────────────────────────────────────
/**
 * Pre-built request options for common API calls.
 */
export const endpoints = {
    // Auth
    login: (email, password) => ({
        method: 'POST',
        path: '/auth/login',
        body: { email, password },
        skipAuth: true,
    }),
    register: (username, email, password) => ({
        method: 'POST',
        path: '/auth/register',
        body: { username, email, password },
        skipAuth: true,
    }),
    refreshToken: (refreshToken) => ({
        method: 'POST',
        path: '/auth/refresh',
        body: { refreshToken },
        skipAuth: true,
    }),
    logout: (refreshToken) => ({
        method: 'POST',
        path: '/auth/logout',
        body: { refreshToken },
    }),
    // User
    getProfile: () => ({
        method: 'GET',
        path: '/users/me',
    }),
    updateProfile: (updates) => ({
        method: 'PATCH',
        path: '/users/me',
        body: updates,
    }),
    // Matches
    getMatches: (page = 1, limit = 20) => ({
        method: 'GET',
        path: '/matches',
        params: { page, limit },
    }),
    getMatch: (matchId) => ({
        method: 'GET',
        path: `/matches/${matchId}`,
    }),
    // Leaderboard
    getLeaderboard: (page = 1, limit = 20) => ({
        method: 'GET',
        path: '/leaderboard',
        params: { page, limit },
        skipAuth: true,
    }),
    getMyRank: () => ({
        method: 'GET',
        path: '/leaderboard/me',
    }),
};
//# sourceMappingURL=api.js.map